/**
 * DuxProvider — Dux analytics integration for the hardware store theme.
 *
 * Loads the Dux client library from Shopify's CDN, initialises tracking with
 * shop context, and syncs consent state with Shopify's native Customer Privacy
 * API.
 *
 * NOTE: Do not pass customer-derived identity from the theme. We set
 * enableDenormalization, and on that path Dux builds the proto user context
 * straight from the client payload with no server-side override, so whatever
 * the theme asserts is what lands in Event Refinery:
 *
 * - `userId` maps onto SourceUserContext.admin_user (an Admin User ID field),
 *   so a storefront customer ID would be recorded as a merchant admin user.
 * - `identityUuid` maps onto SourceUserContext.identity_account.uuid, which
 *   must be a real Shopify Identity UUID.
 *
 * Dux does not resolve either field for this path today; the only trusted
 * enrichment our events receive is employee-impersonation detection. See the
 * Event Refinery governance review for EVENT_APP_HARDWARE_STORE.
 */
class DuxProvider extends HTMLElement {
  static #SERVICE = "horizon-hardware-store";
  static #EVENT_HANDLER_ENDPOINT = "https://www.shopify.com/.well-known/dux?v2";
  static #CDN_URL =
    "https://cdn.shopify.com/shopifycloud/dux/dux-portal-7.5.0.min.js";
  static #PROTO_EVENT_SOURCE = "EVENT_APP_HARDWARE_STORE";

  /**
   * Every Dux logger is on by default. These two emit unbounded free-form text
   * into Event Refinery and have no analytics value on a storefront:
   *
   * - `error` emits `errorMessage` plus the raw, unsanitised `errorStack` and
   *   the full `location.href`. Any theme code that throws with a value in the
   *   message would silently ship that value to the compliant sink.
   * - `form` emits the form `action` and `message`, where `message` is
   *   `"<field>: <browser validation message>"` from any failed submit.
   *
   * Keep the remaining loggers (page, click, scroll, webVitals, visibility,
   * componentViewability): they emit bounded, structured fields only.
   */
  static #DISABLED_LOGGERS = { error: true, form: true };

  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;
    document.addEventListener("dux_consent_ready", this.#syncConsent, {
      signal,
    });
    document.addEventListener("dux_consent_changed", this.#syncConsent, {
      signal,
    });

    this.#init();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  async #init() {
    try {
      await this.#loadScript(DuxProvider.#CDN_URL);
      const countryCode = await this.#getUserCountryCode();
      // @ts-ignore
      window.Dux.init({
        service: DuxProvider.#SERVICE,
        mode: this.dataset.environment || "development",
        eventHandlerEndpoint: DuxProvider.#EVENT_HANDLER_ENDPOINT,
        enableGtm: false,
        enableGtmLoader: false,
        enableActiveConsent: true,
        enableDenormalization: true,
        enableConsentBuffer: true,
        disableLegacyTracking: true,
        disableLogger: DuxProvider.#DISABLED_LOGGERS,
        protoEventSource: DuxProvider.#PROTO_EVENT_SOURCE,
        protoSessionAppType: "merchant",
        // enableSessionWrites / disableLegacyCookies need dux-portal >= 6.4.0
        // (see #CDN_URL). Older bundles ignore them silently and go back to
        // writing the legacy _shopify_y / _shopify_s / _shopify_essential_ cookies.
        enableSessionWrites: true,
        disableLegacyCookies: true,
        countryCode,
        locale: this.dataset.userLocale,
        shopId: this.dataset.shopId,
      });
    } catch (error) {
      console.warn("Dux failed to initialise:", error);
    }
  }

  /** @param {string} src */
  #loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.type = "module";
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  #getUserCountryCode() {
    return fetch(
      window.Shopify.routes.root + "browsing_context_suggestions.json",
    )
      .then((res) => res.json())
      .then((data) => data?.detected_values?.country?.handle || "GB");
  }

  /** @param {Event} evt */
  #syncConsent = (evt) => {
    const { canLoadAnalytics, canLoadMarketing, canLoadFunctional } =
      /** @type {CustomEvent} */ (evt).detail || {};
    window.Shopify.loadFeatures(
      [{ name: "consent-tracking-api", version: "0.1" }],
      (error) => {
        if (error || !window.Shopify?.customerPrivacy) return;
        try {
          window.Shopify.customerPrivacy.setTrackingConsent(
            {
              analytics: canLoadAnalytics,
              marketing: canLoadMarketing,
              preferences: canLoadFunctional,
            },
            () => {},
          );
        } catch (e) {}
      },
    );
  };
}

customElements.define("dux-provider", DuxProvider);
export { DuxProvider };
